/**
 * OnboardingDialog — perfil em etapas (proposta "Onboarding" do Claude Design, 17/10/2026).
 * Substitui a pesquisa de uma tela só (CustomerDialog / showCustomer).
 *
 *  - "novo": setor → segmento → para quem vende → onde estão os clientes → como conheceu.
 *  - "completar": quem já respondeu o questionário antigo vê só o que falta, com o resto
 *    preenchido.
 * A última pergunta tem "Concluir": salva (POST /app/customer), fecha a janela e abre "Sua
 * primeira busca". Sem o resumo "Tudo pronto" nem a dica "Enter para continuar" (27/10/2026, a
 * pedido do Marcelo); o Enter continua avançando.
 *
 * O backend recebe as chaves antigas (industry/customer/aware) e os campos novos (segment, reach,
 * place). O lugar vem do IP (place.ts) e só serve para o perfil e para pré-preencher a busca.
 *
 * Movimento (23/10/2026, design system de movimento): véu e janela grande (.ds-veu, .ds-dialogo
 * .ds-grande) entram com .is-aberto; ao concluir, só a janela sai (200 ms) e o véu fica para
 * "Sua primeira busca", que abre em seguida por cima dele. A barra do topo anda até o passo real
 * (.ds-progresso) e o número do passo rola (Rola, .ds-rola); a pergunta nova troca no lugar com
 * fade (.ds-aparece), sem deslizar; cartões com .ds-escolhivel.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBrand } from '../../hooks/useBrand';
import { usePresenca } from '../../hooks/usePresenca';
import { tokenMs } from '../../lib/movimento';
import { Rola } from '../lists/Rola';
import { useCustomer } from '../../api/useFeedback';
import { emitActivity } from '../../lib/activity';
import type { MeProfile } from '../../types/session';
import {
  CUSTOMERS,
  ORIGINS,
  ORIGIN_GROUPS,
  REACHES,
  SECTORS,
  SECTOR_BY_ID,
  customerByLegacy,
  findSegment,
  isReach,
  norm,
  sectorByIndustry,
  type CustomerId,
  type Origin,
  type ReachId,
  type Sector,
  type Segment,
} from './catalog';
import {
  nextStep,
  planFlow,
  prevStep,
  stepNumber,
  type OnboardingMode,
  type QuestionStep,
} from './flow';
import { userPlace } from './place';
import { faviconFor } from '../../lib/favicons';
import { assetUrls } from '../../config/env';
import { useOnboardingText, type OnboardingKey } from './onboardingText';
import './onboarding.css';

export interface OnboardingResult {
  sectorId: string | null;
  segmentId: string | null;
  /** Segmento como a pessoa vê (traduzido ou o texto do "Outro"). */
  segmentLabel: string;
  customer: CustomerId;
  reach: ReachId;
}

interface OnboardingDialogProps {
  mode: OnboardingMode;
  profile?: MeProfile;
  onDone: (result: OnboardingResult) => void;
}

type Status = null | 'saving' | 'error' | 'offline';

const VALIDATION: Record<QuestionStep, OnboardingKey> = {
  setor: 'val_sector',
  segmento: 'val_segment',
  cliente: 'val_customer',
  local: 'val_reach',
  origem: 'val_origin',
};

/** Acima disso a lista vira ordem alfabética. */
const LONG_LIST = 12;
/** Marca no lugar do número em "Passo {{n}} de {{total}}", para o número rolar sozinho. */
const MARCA = '\u2063';

function initialState(profile: MeProfile | undefined) {
  const sector = sectorByIndustry(profile?.industry);
  const found = findSegment(profile?.segment);
  const ownSegment = Boolean(profile?.segment) && !found;
  return {
    sectorId: found?.sector.id ?? sector?.id ?? null,
    segmentId: found?.segment.id ?? null,
    other: ownSegment || sector?.id === 'out',
    otherText: ownSegment ? (profile?.segment ?? '') : '',
    customer: customerByLegacy(profile?.customer) ?? null,
    reach: isReach(profile?.reach) ? profile.reach : null,
  };
}

export function OnboardingDialog({ mode, profile, onDone }: OnboardingDialogProps): JSX.Element {
  const { tx, sectorName, segmentName, lang } = useOnboardingText();
  const brand = useBrand();
  const save = useCustomer();
  const flow = useMemo(() => planFlow(mode, profile), [mode, profile]);
  const place = useMemo(() => userPlace(), []);
  const init = useMemo(() => initialState(profile), [profile]);

  const [step, setStep] = useState<QuestionStep>(flow.start);
  const [sectorId, setSectorId] = useState<string | null>(init.sectorId);
  const [segmentId, setSegmentId] = useState<string | null>(init.segmentId);
  const [other, setOther] = useState(init.other);
  const [otherText, setOtherText] = useState(init.otherText);
  const [customer, setCustomer] = useState<CustomerId | null>(init.customer);
  const [reach, setReach] = useState<ReachId | null>(init.reach);
  const [origin, setOrigin] = useState<string | null>(null);
  const [detail, setDetail] = useState('');
  const [status, setStatus] = useState<Status>(null);
  const [tried, setTried] = useState(false);
  // Movimento: se já trocou de passo (a primeira pergunta chega com a janela, sem entrada
  // própria) e a saída da janela ao concluir.
  const [trocou, setTrocou] = useState(false);
  const [saindo, setSaindo] = useState(false);
  const { aberto } = usePresenca(!saindo, '--duracao-200');
  const concluirRef = useRef(0);
  useEffect(() => () => window.clearTimeout(concluirRef.current), []);
  const [slowSave, setSlowSave] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const otherRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (status !== 'saving') {
      setSlowSave(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setSlowSave(true), 200);
    return () => window.clearTimeout(timer);
  }, [status]);

  const sector: Sector | null = sectorId ? (SECTOR_BY_ID[sectorId] ?? null) : null;
  const segment: Segment | null = sector?.segments.find((s) => s.id === segmentId) ?? null;
  const originDef: Origin | null = ORIGINS.find((o) => o.id === origin) ?? null;
  const product = brand.appTitle;

  useEffect(() => {
    bodyRef.current?.scrollTo?.({ top: 0 });
  }, [step, mode]);

  const valid = useMemo<Record<QuestionStep, boolean>>(
    () => ({
      setor: Boolean(sectorId),
      segmento: other ? norm(otherText).length > 1 : Boolean(segmentId),
      cliente: Boolean(customer),
      local: Boolean(reach),
      origem: Boolean(origin),
    }),
    [sectorId, other, otherText, segmentId, customer, reach, origin],
  );

  const segmentLabel = other ? otherText.trim() : segment ? segmentName(segment) : '';

  // O campo "Qual?" recebe o foco quando aparece. Não é autoFocus: se ele nasce junto com a
  // janela (completar, setor "Outros"), ela ainda está invisível (.ds-dialogo sem .is-aberto) e
  // o navegador recusa o foco; aqui ele espera a janela abrir.
  useEffect(() => {
    if (aberto && other && step === 'segmento') otherRef.current?.focus();
  }, [aberto, other, step]);

  /** Troca de passo; a pergunta nova entra com fade (.ds-aparece). */
  const irPara = useCallback(
    (target: QuestionStep) => {
      if (target === step) return;
      setTrocou(true);
      setStep(target);
    },
    [step],
  );

  /** Perfil salvo: a janela sai e "Sua primeira busca" abre (o que era o "Concluir" do resumo). */
  const finish = useCallback(() => {
    if (!customer || !reach) return;
    const result: OnboardingResult = {
      sectorId,
      segmentId: other ? null : segmentId,
      segmentLabel,
      customer,
      reach,
    };
    // Movimento: a janela sai (200 ms, o degrau de baixo dos 280 da entrada) com o véu parado;
    // "Sua primeira busca" abre em seguida sobre o mesmo véu. Sem duração (testes), na hora.
    const ms = tokenMs('--duracao-200');
    if (ms <= 0) {
      onDone(result);
      return;
    }
    setSaindo(true);
    // Cancelado se a janela desmontar antes (troca de sessão ou de rota).
    concluirRef.current = window.setTimeout(() => onDone(result), ms);
  }, [customer, reach, sectorId, other, segmentId, segmentLabel, onDone]);

  const submit = useCallback(() => {
    const sectorDef = sectorId ? SECTOR_BY_ID[sectorId] : undefined;
    const customerDef = CUSTOMERS.find((c) => c.id === customer);
    setStatus('saving');
    emitActivity('onboarding.step_completed', { step: 'perfil', source: String(mode).slice(0, 20) });
    save.mutate(
      {
        industry: sectorDef?.industry,
        customer: customerDef?.legacy,
        segment: other ? otherText.trim() : segment?.name,
        reach: reach ?? undefined,
        place:
          place.city || place.country
            ? { city: place.city, region: place.region, country: place.country }
            : undefined,
        aware: mode === 'novo' ? originDef?.aware : undefined,
        other: mode === 'novo' && detail.trim() ? detail.trim() : undefined,
      },
      {
        onSuccess: () => {
          setStatus(null);
          finish();
        },
        onError: () =>
          setStatus(
            typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'error',
          ),
      },
    );
  }, [
    sectorId,
    customer,
    mode,
    save,
    other,
    otherText,
    segment,
    reach,
    place,
    originDef,
    detail,
    finish,
  ]);

  const advance = useCallback(() => {
    if (status === 'saving' || saindo) return;
    if (!valid[step]) {
      setTried(true);
      return;
    }
    setTried(false);
    const next = nextStep(flow, step);
    if (!next) {
      submit();
      return;
    }
    setStatus(null);
    irPara(next);
  }, [status, saindo, step, valid, flow, submit, irPara]);

  // Enter avança (menos dentro de botões, que já respondem ao Enter sozinhos).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Enter' || e.isComposing) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'BUTTON' || target.tagName === 'TEXTAREA')) return;
      e.preventDefault();
      advance();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [advance]);

  /** Escolha que leva direto ao próximo passo (setor, cliente, alcance). */
  function pickAndGo(apply: () => void, current: QuestionStep): void {
    apply();
    setTried(false);
    const next = nextStep(flow, current);
    if (!next) return;
    irPara(next);
  }

  function pickSector(id: string): void {
    if (id !== sectorId) {
      setSectorId(id);
      setSegmentId(null);
      setOther(id === 'out');
      setOtherText('');
    }
    pickAndGo(() => undefined, 'setor');
  }

  // Só as opções do setor, sem procura nem contagem (27/10/2026); lista longa em ordem alfabética.
  const ownList = sector?.segments ?? [];
  const sortedList =
    ownList.length > LONG_LIST
      ? ownList.slice().sort((a, b) => segmentName(a).localeCompare(segmentName(b), lang))
      : ownList;

  const totalSteps = flow.groups.length;
  const currentNumber = stepNumber(flow, step);
  const canBack = prevStep(flow, step) !== null;
  const canGo = valid[step];
  // Última pergunta do fluxo: o botão vira "Concluir" (salva, fecha e abre "Sua primeira busca").
  const isLast = nextStep(flow, step) === null;

  const originLabel = (o: Origin): string => o.brand ?? tx(`o_${o.id}` as OnboardingKey);

  // "Passo 2 de 4" com o número rolando: o texto visível fica aria-hidden e o leitor de tela lê
  // a frase inteira (.ob-sr). Se a tradução não tiver o número, a frase vai inteira, sem rolar.
  const stepText = tx('step_of', { n: currentNumber, total: totalSteps });
  const stepParts = tx('step_of', { n: MARCA, total: totalSteps }).split(MARCA);
  // Rótulo do botão principal. O giro só aparece se salvar passar de 200 ms (espera do guia):
  // resposta mais rápida fecha a janela direto, sem piscar.
  const label: 'go' | 'saving' | 'retry' =
    status === 'saving' ? (slowSave ? 'saving' : 'go') : status ? 'retry' : 'go';

  // Cada pergunta é uma <section> própria: ao trocar de passo ela monta de novo e troca no lugar
  // com fade (.ds-aparece, 200 ms), sem deslizar. A primeira chega com a janela.
  const passo = `ob-section${trocou ? ' ds-aparece' : ''}`;

  return createPortal(
    <div className={`ob-overlay ds-veu${aberto || saindo ? ' is-aberto' : ''}`} role="presentation">
      <div
        className={`ob-dialog ds-dialogo ds-grande${aberto ? ' is-aberto' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ob-title"
      >
        <div className="ob-head">
          <div className="ob-head__row">
            <span className="ob-brand">
              {/* O logo do cabeçalho (o mesmo nas duas marcas desde 17/10/2026), não o favicon,
                  que em homologação é o "DEV". O nome ao lado é o da marca. */}
              <img src={assetUrls.logoLeadMachine} alt="" width={28} height={28} />
              <strong>{product}</strong>
            </span>
            <span className="ob-step">
              <strong>
                {stepParts.length === 2 ? (
                  <>
                    <span aria-hidden="true">
                      {stepParts[0]}
                      <Rola valor={currentNumber} />
                      {stepParts[1]}
                    </span>
                    <span className="ob-sr">{stepText}</span>
                  </>
                ) : (
                  stepText
                )}
              </strong>
            </span>
          </div>
          {/* Progresso real (.ds-progresso): o trecho do passo cresce da esquerda em 400 ms. */}
          <div className="ob-bars" aria-hidden="true">
            {flow.groups.map((g, i) => (
              <span key={g} className="ds-progresso">
                <span
                  style={{ transform: `scaleX(${i < currentNumber ? 1 : 0})` }}
                />
              </span>
            ))}
          </div>
        </div>

        <div className="ob-body" ref={bodyRef}>
          {mode === 'completar' && step === flow.start ? (
            <div className="ob-callout">
              <span className="material-symbols-rounded" aria-hidden="true">
                auto_awesome
              </span>
              <div>
                <strong>{tx('complete_title')}</strong>
                <p>{tx('complete_text')}</p>
              </div>
            </div>
          ) : null}

          {step === 'setor' ? (
            <section className={passo}>
              <div className="ob-title">
                <h2 id="ob-title">{tx('sector_title')}</h2>
              </div>
              <div className="ob-sectors">
                {SECTORS.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`ob-card ob-card--sector ds-escolhivel${sectorId === s.id ? ' is-escolhido' : ''}`}
                    aria-pressed={sectorId === s.id}
                    onClick={() => pickSector(s.id)}
                  >
                    <span className="ob-card__icon material-symbols-rounded" aria-hidden="true">
                      {s.icon}
                    </span>
                    <span className="ob-card__name">{sectorName(s)}</span>
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          {step === 'segmento' ? (
            <section className={passo}>
              <div className="ob-title">
                <h2 id="ob-title">{tx('segment_title')}</h2>
              </div>

              {sortedList.length > 0 ? (
                <div className="ob-chips">
                  {sortedList.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className={`ob-chip${!other && segmentId === s.id ? ' is-on' : ''}`}
                      aria-pressed={!other && segmentId === s.id}
                      onClick={() => {
                        setSegmentId(s.id);
                        setOther(false);
                        setTried(false);
                      }}
                    >
                      {segmentName(s)}
                    </button>
                  ))}
                </div>
              ) : null}

              {sector && sector.segments.length > 0 ? (
                <div className="ob-chips">
                  <button
                    type="button"
                    className={`ob-chip ob-chip--other${other ? ' is-on' : ''}`}
                    aria-pressed={other}
                    onClick={() => {
                      setOther(true);
                      setSegmentId(null);
                      setTried(false);
                    }}
                  >
                    <span className="material-symbols-rounded" aria-hidden="true">
                      add
                    </span>
                    {tx('other_which')}
                  </button>
                </div>
              ) : null}

              {other ? (
                <label className="ob-field ds-aparece">
                  <span>{tx('other_label')}</span>
                  <input
                    ref={otherRef}
                    type="text"
                    value={otherText}
                    maxLength={80}
                    placeholder={tx('other_placeholder')}
                    onChange={(e) => {
                      setOtherText(e.target.value);
                      setTried(false);
                    }}
                  />
                </label>
              ) : null}
            </section>
          ) : null}

          {step === 'cliente' ? (
            <section className={passo}>
              <div className="ob-title">
                <h2 id="ob-title">{tx('customer_title')}</h2>
              </div>
              <div className="ob-options">
                {CUSTOMERS.map((c) => (
                  <OptionCard
                    key={c.id}
                    icon={c.icon}
                    title={tx(`c_${c.id}` as OnboardingKey)}
                    text={tx(`c_${c.id}_text` as OnboardingKey)}
                    on={customer === c.id}
                    onClick={() => pickAndGo(() => setCustomer(c.id), 'cliente')}
                  />
                ))}
              </div>
            </section>
          ) : null}

          {step === 'local' ? (
            <section className={passo}>
              <div className="ob-title">
                <h2 id="ob-title">{tx('reach_title')}</h2>
              </div>
              <div className="ob-options">
                {REACHES.map((r) => (
                  <OptionCard
                    key={r.id}
                    icon={r.icon}
                    wide={r.wide}
                    title={tx(`r_${r.id}` as OnboardingKey)}
                    text={tx(`r_${r.id}_text` as OnboardingKey)}
                    on={reach === r.id}
                    onClick={() => pickAndGo(() => setReach(r.id), 'local')}
                  />
                ))}
              </div>
            </section>
          ) : null}

          {step === 'origem' ? (
            <section className={`${passo} ob-section--tight`}>
              <div className="ob-title">
                <h2 id="ob-title">{tx('origin_title', { product })}</h2>
                <p>{tx('origin_text')}</p>
              </div>
              {ORIGIN_GROUPS.map((g) => (
                <div key={g} className="ob-group">
                  <span className="ob-eyebrow">{tx(`g_${g}` as OnboardingKey)}</span>
                  <div className="ob-chips">
                    {ORIGINS.filter((o) => o.group === g).map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        className={`ob-chip ob-chip--origin${origin === o.id ? ' is-on' : ''}`}
                        aria-pressed={origin === o.id}
                        onClick={() => {
                          if (origin !== o.id) setDetail('');
                          setOrigin(o.id);
                          setTried(false);
                        }}
                      >
                        <OriginMark origin={o} />
                        {originLabel(o)}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {originDef?.detail ? (
                <label className="ob-field ob-field--panel ds-aparece">
                  <span>
                    {tx(`d_${originDef.id}` as OnboardingKey)} <em>{tx('optional')}</em>
                  </span>
                  <input
                    type="text"
                    value={detail}
                    maxLength={200}
                    autoFocus
                    onChange={(e) => setDetail(e.target.value)}
                  />
                </label>
              ) : null}
            </section>
          ) : null}

          {tried && !valid[step] ? (
            <p className="ob-validation ds-aparece" role="alert">
              {tx(step === 'segmento' && other ? 'val_other' : VALIDATION[step])}
            </p>
          ) : null}

          {status === 'error' || status === 'offline' ? (
            <p className={`ob-status ob-status--${status} ds-aparece`} role="alert">
              <span className="material-symbols-rounded" aria-hidden="true">
                {status === 'offline' ? 'wifi_off' : 'error'}
              </span>
              {tx(status === 'offline' ? 'offline' : 'save_error')}
            </p>
          ) : null}
        </div>

        <div className="ob-foot">
          {canBack ? (
            <button
              type="button"
              className="ob-back ds-resposta"
              onClick={() => {
                const prev = prevStep(flow, step);
                if (prev) {
                  setTried(false);
                  setStatus(null);
                  irPara(prev);
                }
              }}
            >
              <span className="material-symbols-rounded" aria-hidden="true">
                arrow_back
              </span>
              {tx('back')}
            </button>
          ) : (
            <span />
          )}
          <div className="ob-foot__go">
            <button
              type="button"
              className={`ob-primary ds-resposta${canGo ? '' : ' is-off'}`}
              aria-disabled={!canGo}
              disabled={status === 'saving'}
              aria-busy={status === 'saving' || undefined}
              onClick={() => (status === 'error' || status === 'offline' ? submit() : advance())}
            >
              {/* Carregando no botão: os três rótulos no mesmo lugar (.ds-troca, 140 ms), sem
                  mudar a largura; o leitor de tela lê só o atual. */}
              <span className="ob-primary__troca ds-troca ds-troca-texto">
                <span
                  className={label === 'saving' ? undefined : 'ds-fora'}
                  aria-hidden={label !== 'saving' || undefined}
                >
                  <span className="ob-spinner ds-giro" aria-hidden="true" />
                  {tx('saving')}
                </span>
                <span
                  className={label === 'retry' ? undefined : 'ds-fora'}
                  aria-hidden={label !== 'retry' || undefined}
                >
                  <span className="material-symbols-rounded" aria-hidden="true">
                    refresh
                  </span>
                  {tx('retry')}
                </span>
                <span
                  className={label === 'go' ? undefined : 'ds-fora'}
                  aria-hidden={label !== 'go' || undefined}
                >
                  {isLast ? tx('finish') : tx('continue')}
                  <span className="material-symbols-rounded" aria-hidden="true">
                    arrow_forward
                  </span>
                </span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function OptionCard({
  icon,
  title,
  text,
  on,
  wide,
  onClick,
}: {
  icon: string;
  title: string;
  text: string;
  on: boolean;
  wide?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`ob-card ob-card--option ds-escolhivel${on ? ' is-escolhido' : ''}${wide ? ' is-wide' : ''}`}
      aria-pressed={on}
      onClick={onClick}
    >
      <span className="ob-card__icon material-symbols-rounded" aria-hidden="true">
        {icon}
      </span>
      <span className="ob-card__text">
        <strong>{title}</strong>
        <span>{text}</span>
      </span>
      {on ? (
        <span className="ob-card__check material-symbols-rounded" aria-hidden="true">
          check
        </span>
      ) : null}
    </button>
  );
}

/** Favicon da marca (src/assets/favicons; a inicial se não houver) ou o ícone da opção. */
function OriginMark({ origin }: { origin: Origin }): JSX.Element {
  const icon = faviconFor(origin.domain);
  if (icon) {
    return (
      <span className="ob-mark ob-mark--logo" aria-hidden="true">
        <img src={icon} alt="" width={16} height={16} />
      </span>
    );
  }
  if (origin.brand) {
    return (
      <span className="ob-mark" aria-hidden="true">
        {origin.brand.charAt(0)}
      </span>
    );
  }
  return (
    <span className="ob-mark material-symbols-rounded" aria-hidden="true">
      {origin.icon}
    </span>
  );
}
